<template>
    <UiModal
        :is-open="open"
        :title="modalTitle"
        @close="emit('close')"
        :is-teleported="true"
    >
        <template #title>
            <div class="flex items-center gap-4">
                <h5 id="modal-title" class="ui-modal-title">
                    {{ modalTitle }}
                </h5>
                <button
                    class="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                    @click="handleAddBusiness"
                >
                    &plus; Add
                </button>
            </div>
        </template>
        <template #content>
            <div class="relative">
                <LoadingState v-if="isWalletBusy" :loading="isWalletBusy" />
                <div
                    class="space-y-4"
                    v-if="!isWalletBusy && totalBusinessCount > 0"
                >
                    <div
                        class="mb-8"
                        v-for="user in displayUsers"
                        :key="user.id"
                    >
                        <div
                            class="flex items-center gap-2 mb-2"
                            v-if="displayUsers.length > 1"
                        >
                            <Icon icon="person" class="text-slate-500 dark:text-slate-400" />
                            <span class="text-sm text-slate-800 dark:text-slate-100">
                                {{ user.name }}'s business entities
                            </span>
                        </div>
                        <div class="" v-if="user.businesses.length === 0">
                            <p class="text-sm text-slate-500 dark:text-slate-400 mb-2">
                                Add a business entity to keep track of all related
                                cards.
                            </p>
                            <button
                                type="button"
                                class="btn btn-secondary !p-2 !py-1 text-sx flex items-center gap-2"
                                @click="handleAddBusiness"
                            >
                                <Icon
                                    icon="business"
                                    :width="16"
                                    :height="16"
                                />
                                Add Business Entity
                            </button>
                        </div>
                        <ul v-else class="w-full space-y-4">
                            <li
                                class="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 pt-4 first:border-none"
                                v-for="business in user.businesses"
                                :key="business.uuid"
                            >
                                <div class="flex items-center gap-2">
                                    <div
                                        class="w-10 h-10 rounded-full flex items-center justify-center bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                                    >
                                        <Icon icon="business" />
                                    </div>
                                    <button
                                        type="button"
                                        class="text-left hover:text-slate-400 transition-colors"
                                        @click="editBusiness(business)"
                                    >
                                        <h5
                                            class="font-medium text-sm text-slate-800 dark:text-slate-100"
                                        >
                                            {{ business.name }}
                                        </h5>
                                        <p class="text-sm text-slate-500 dark:text-slate-400">
                                            {{
                                                businessTypeName(business.type)
                                            }}
                                        </p>
                                    </button>
                                </div>
                                <div class="flex items-center gap-4">
                                    <button
                                        class="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                                        @click.prevent="editBusiness(business)"
                                    >
                                        <Icon
                                            icon="edit"
                                            :width="20"
                                            :height="20"
                                        />
                                        <span class="sr-only">Edit</span>
                                    </button>
                                    <button
                                        class="text-slate-500 dark:text-slate-400 hover:text-red-800 dark:hover:text-red-400 transition-colors"
                                        @click.prevent="
                                            emit(
                                                'show-delete-confirm',
                                                business
                                            )
                                        "
                                    >
                                        <Icon icon="delete" />
                                        <span class="sr-only">Delete</span>
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div
                    class="flex flex-col items-center justify-center"
                    v-else-if="!isWalletBusy"
                >
                    <div
                        class="flex items-center justify-center w-14 h-14 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 mb-6"
                    >
                        <Icon icon="business" :width="24" :height="24" />
                    </div>
                    <h5 class="font-medium text-slate-800 dark:text-slate-100 mb-2">
                        No business entities yet
                    </h5>
                    <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
                        Add your first business entity to keep track of all related
                        cards.
                    </p>
                    <button
                        type="button"
                        class="btn btn-secondary flex items-center gap-2"
                        @click="handleAddBusiness"
                    >
                        <Icon icon="business" :width="18" :height="18" />
                        Add Business Entity
                    </button>
                </div>
            </div>
        </template>
    </UiModal>
</template>
<script setup>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import Icon from '@/components/ui/icon/icon.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import { useWalletStore } from '@/store/wallet';

const walletStore = useWalletStore();
const {
    loadingWallet,
    refreshingWallet,
    businessEntities,
    loadingBusinessEntities,
} = storeToRefs(walletStore);

// MY1-2830: business CRUD now refreshes the wallet in background mode, so the
// spinner tracks both the cold load and a background refresh.
// SUPPORT-739: it also covers the fresh business entities fetch on open.
const isWalletBusy = computed(
    () =>
        loadingWallet.value ||
        refreshingWallet.value ||
        loadingBusinessEntities.value
);

const props = defineProps({
    open: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    'close',
    'add-new-business',
    'edit-business',
    'show-delete-confirm',
]);

const modalTitle = ref('Manage Business Entities');

// SUPPORT-739: render the fresh `/businesses/users` list, not the wallet
// payload's embedded users. The wallet payload can be a stale snapshot (server
// cache, IndexedDB, or a long-lived tab's in-memory state), and acting on a
// stale row 404s — this modal is where entities get edited/deleted, so it must
// reflect the database. `businesses` is normalized so a payload missing the key
// (SUPPORT-710 allowlist family) degrades to an empty list instead of crashing.
const displayUsers = computed(() => {
    return (businessEntities.value || []).map((user) => ({
        ...user,
        businesses: user.businesses || [],
    }));
});

watch(
    () => props.open,
    (open) => {
        if (open) {
            walletStore.getBusinessEntitiesByUser();
        }
    },
    { immediate: true }
);

const totalBusinessCount = computed(() => {
    return displayUsers.value.reduce((total, user) => {
        return total + (user.businesses ? user.businesses.length : 0);
    }, 0);
});

const handleAddBusiness = () => {
    emit('add-new-business');
};

const businessTypeName = (type) => {
    switch (type) {
        case 'sole-prop':
            return 'Sole Proprietorship';
        case 'partnership':
            return 'Partnership';
        case 'llc':
            return 'Limited Liability Company';
        case 's-corp':
            return 'S-Corp';
        case 'c-corp':
            return 'C-Corp';
        default:
            return 'Unknown';
    }
};

const editBusiness = (business) => {
    emit('edit-business', business);
};

const close = () => {
    emit('close');
};
</script>
